import * as React from "react";
import { styled } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableFooter from "@mui/material/TableFooter";
import TableRow from "@mui/material/TableRow";
import { Helmet } from "react-helmet";
import {
  Button,
  Chip,
  Pagination,
  Tab,
  TableHead,
  Tabs,
  Typography
} from "@mui/material";
import useAxios from "../../components/UseAxios/useAxios";
import { useCallback, useEffect, useState } from "react";
import { ITimeOffType } from "../../models/timeofftype";
import { toggleMessage } from "../../components/Toast/Toast";
import {
  FeatureModel,
  PayRollPermissionModel
} from "../../models/common/models.enum";
import { IFeaturePermission } from "../../models/user/models.roles";
import RoleService from "../../services/api/role.service";
import { authTokens } from "../../services/services";
import LoadingOverlayWrapper from "react-loading-overlay-ts";
import { useTranslation } from "react-i18next";
import CreateTimeOffTypeModal from "../TimeOffType/components/CreateTimeOffTypeModal/CreateTimeOffTypeModal";
import { IPayroll } from "@/models/payroll";
import PayrollService from "../../services/api/payroll.service";
import PayRollModal from "./PayRollModal";
import dayjs from "dayjs";

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    fontSize: 14,
    lineHeight: 1.5
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    lineHeight: 1.5
  }
}));

export default function PayRoll(): JSX.Element {
  const { t } = useTranslation();

  const [page, setPage] = React.useState(1);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [permission, setPermission] = useState<
    IFeaturePermission | undefined
  >();
  const [openModalImportPayroll, setOpenModalImportPayroll] =
    useState<boolean>(false);

  const [openModalTranslate, setOpenModalTranslate] = useState<boolean>(false);

  const [data, setData] = useState<IPayroll[]>([]);
  const getPermissionAxios = useAxios<IFeaturePermission[]>({
    loading: "OnRequest"
  });
  const getPagePayroll = useAxios<IPayroll[]>({
    loading: "OnRequest"
  });

  const base64ToDownloadUrl = (base64: string, fileName: string) => {
    const arr = base64.split(",");
    const mime = arr[0].match(/:(.*?);/)?.[1] || "";
    const bstr = atob(arr[1]);
    let n = bstr.length;
    const u8arr = new Uint8Array(n);

    while (n--) {
      u8arr[n] = bstr.charCodeAt(n);
    }

    const file = new Blob([u8arr], { type: mime });
    return URL.createObjectURL(file);
  };

  useEffect(() => {
    authTokens.getUser().then(user => {
      user &&
        getPermissionAxios.request(
          RoleService.getFeaturePemissionByUserId(user.userId ?? "")
        );
    });
  }, []);

  const fetchPayrollTypePage = useCallback(() => {
    if (permission?.permissions.includes(PayRollPermissionModel.View))
      getPagePayroll.request(
        PayrollService.getPage({ CurrentPage: page, PageSize: rowsPerPage })
      );
  }, [permission, page, rowsPerPage, getPagePayroll]);

  useEffect(() => {
    fetchPayrollTypePage();
  }, [permission]);

  useEffect(() => {
    if (getPermissionAxios.isSuccess && getPermissionAxios.data) {
      setPermission(
        getPermissionAxios.data.find(e => e.feature === FeatureModel.Payroll)
      );
    }
    if (getPermissionAxios.error) {
      toggleMessage({
        type: "error",
        message: getPermissionAxios.error?.message ?? ""
      });
    }
  }, [
    getPermissionAxios.isSuccess,
    getPermissionAxios.error,
    getPermissionAxios.data
  ]);

  useEffect(() => {
    if (getPagePayroll.isSuccess && getPagePayroll.data) {
      setData(getPagePayroll.data);
    }
    if (getPagePayroll.error) {
      toggleMessage({
        type: "error",
        message: getPagePayroll.error.message ?? ""
      });
    }
  }, [getPagePayroll.isSuccess, getPagePayroll.data, getPagePayroll.error]);

  const emptyRows = data.length === 0;

  const handleCreate = () => {
    setOpenModalImportPayroll(true);
  };

  const handleTranslate = () => {
    setOpenModalTranslate(true);
  };

  const handleCloseMoal = () => {
    setOpenModalImportPayroll(false);
  };

  const handleCloseMoalTranslate = () => {
    setOpenModalTranslate(false);
  };

  return (
    <Box className={"flex flex-col"} sx={{ padding: "12px 26px 8px 26px" }}>
      <Helmet>
        <meta charSet="utf-8" />
        <title>{t("Payroll.Title")}</title>
      </Helmet>

      <LoadingOverlayWrapper
        active={getPermissionAxios.isLoading}
        spinner
        text={t("TimeOffType.Loading.Data")}
      >
        {permission && (
          <Table sx={{ minWidth: 500 }} aria-label="custom pagination table">
            <TableHead>
              <TableRow>
                <StyledTableCell colSpan={3}>
                  {t("Payroll.FileName")}
                </StyledTableCell>
                <StyledTableCell colSpan={8}>
                  {t("Payroll.Email")}
                </StyledTableCell>
                <StyledTableCell colSpan={8}>
                  {t("Payroll.CreatedDate")}
                </StyledTableCell>
                <StyledTableCell colSpan={8}>
                  {t("Payroll.Attachments")}
                </StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map(row => (
                <TableRow key={row.fileName}>
                  <StyledTableCell colSpan={3}>{row.fileName}</StyledTableCell>
                  <StyledTableCell colSpan={8}>{row.email}</StyledTableCell>
                  <StyledTableCell colSpan={8}>
                    {dayjs(row.createdDate).format("DD/MM/YYYY HH:mm")}
                  </StyledTableCell>
                  <StyledTableCell colSpan={8}>
                    {row.fileBase64 ? (
                      <a
                        href={base64ToDownloadUrl(row.fileBase64, row.fileName)}
                        download={row.fileName}
                        style={{
                          color: "#1976d2",
                          textDecoration: "underline",
                          cursor: "pointer"
                        }}
                      >
                        Tải file
                      </a>
                    ) : (
                      "Không có file"
                    )}
                  </StyledTableCell>
                </TableRow>
              ))}
              {emptyRows && (
                <TableRow style={{ height: 53 * 14 }}>
                  <TableCell colSpan={6} />
                </TableRow>
              )}
            </TableBody>

            <TableFooter>
              <TableRow>
                {getPagePayroll.paging &&
                  getPagePayroll.paging.totalPages > 1 && (
                    <Pagination
                      count={getPagePayroll.paging.totalPages}
                      page={getPagePayroll.paging.currentPage}
                      sx={{ padding: 1 }}
                      onChange={(e, value) => setPage(value)}
                    />
                  )}
              </TableRow>
            </TableFooter>

            {permission?.permissions.includes(
              PayRollPermissionModel.Create
            ) && (
              <TableContainer className="table-caption">
                <Button
                  className="rounded-lg float-right"
                  variant="contained"
                  color="primary"
                  size="small"
                  sx={{
                    marginRight: 1,
                    marginY: 2,
                    fontSize: 14,
                    height: "auto"
                  }}
                  onClick={handleCreate}
                >
                  {t("Payroll.SendPayroll")}
                </Button>

                <Button
                  className="rounded-lg float-right"
                  variant="contained"
                  color="primary"
                  size="small"
                  sx={{
                    marginRight: 1,
                    marginY: 2,
                    fontSize: 14,
                    height: "auto"
                  }}
                  onClick={handleTranslate}
                >
                  Translate English to VietNam
                </Button>
              </TableContainer>
            )}
          </Table>
        )}
      </LoadingOverlayWrapper>

      {openModalImportPayroll && (
        <PayRollModal
          onClose={() => handleCloseMoal()}
          open={openModalImportPayroll}
          onUpdateSuccess={() => {
            handleCloseMoal();
            fetchPayrollTypePage();
          }}
        />
      )}

      {/* {openModalTranslate && (
        <TranslateModal
          onClose={() => handleCloseMoalTranslate()}
          open={openModalTranslate}
          onUpdateSuccess={() => {
            handleCloseMoalTranslate();
            fetchPayrollTypePage();
          }}
        />
      )} */}
    </Box>
  );
}
